'use client'

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { DesignRequestEnriched } from '@/lib/types/design-request'
import type { Serialized } from '@/lib/db/mappers'
import { fetchApi } from '@/lib/utils/fetch-api'

export type ApiDesignRequest = Serialized<DesignRequestEnriched>

// ============================================
// Query Keys
// ============================================
export const designRequestKeys = {
  all: ['designRequests'] as const,
  lists: () => [...designRequestKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...designRequestKeys.lists(), filters] as const,
  details: () => [...designRequestKeys.all, 'detail'] as const,
  detail: (id: string) => [...designRequestKeys.details(), id] as const,
  // 2026-08-02 [業務承認: matsushima-y owner裁定] ゴミ箱ビュー（論理削除済み）
  trash: () => [...designRequestKeys.all, 'trash'] as const,
}

// ============================================
// Hooks
// ============================================

/**
 * 設計依頼一覧取得
 */
export function useDesignRequests() {
  return useQuery({
    queryKey: designRequestKeys.lists(),
    queryFn: () => fetchApi<ApiDesignRequest[]>('/api/design-requests'),
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * 単一設計依頼取得
 */
export function useDesignRequest(id: string) {
  return useQuery({
    queryKey: designRequestKeys.detail(id),
    queryFn: () => fetchApi<ApiDesignRequest>(`/api/design-requests/${id}`),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * 設計依頼作成
 */
export function useCreateDesignRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: Partial<ApiDesignRequest>) =>
      fetchApi<ApiDesignRequest>('/api/design-requests', {
        method: 'POST',
        body: JSON.stringify(request),
      }),
    onSuccess: (newRequest) => {
      queryClient.setQueryData(designRequestKeys.detail(newRequest.id), newRequest)
      queryClient.invalidateQueries({ queryKey: designRequestKeys.lists() })
    },
  })
}

/**
 * 設計依頼更新
 */
export function useUpdateDesignRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<ApiDesignRequest> }) =>
      fetchApi<ApiDesignRequest>(`/api/design-requests/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      }),
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey: designRequestKeys.lists() })

      const previousList = queryClient.getQueryData<ApiDesignRequest[]>(designRequestKeys.lists())

      if (previousList) {
        queryClient.setQueryData(
          designRequestKeys.lists(),
          previousList.map((r) =>
            r.id === id ? { ...r, ...updates, updatedAt: new Date().toISOString() } : r
          )
        )
      }

      return { previousList }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(designRequestKeys.lists(), context.previousList)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: designRequestKeys.lists() })
    },
  })
}

/**
 * 設計依頼削除
 */
export function useDeleteDesignRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => fetchApi<void>(`/api/design-requests/${id}`, { method: 'DELETE' }),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: designRequestKeys.detail(id) })
      // 2026-08-02 論理削除化に伴い、削除でゴミ箱にも移るため trash も含めて全体を再取得。
      queryClient.invalidateQueries({ queryKey: designRequestKeys.all })
    },
  })
}

/**
 * 2026-08-02 [業務承認: matsushima-y owner裁定] ゴミ箱（論理削除済み）一覧。
 * enabled=false（通常表示）のときは取得しない。設計依頼・orphan プラン依頼の削除済みを同 shape で返す。
 */
export function useDeletedDesignRequests(enabled: boolean) {
  return useQuery({
    queryKey: designRequestKeys.trash(),
    queryFn: () => fetchApi<ApiDesignRequest[]>('/api/design-requests?deleted=true'),
    enabled,
    staleTime: 30 * 1000,
    gcTime: 60 * 1000,
  })
}

/**
 * 2026-08-02 [業務承認: matsushima-y owner裁定] 設計依頼の復元（ゴミ箱→一覧）。
 * サーバ側で紐付く plan_request も対称に復元する。
 */
export function useRestoreDesignRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<void>(`/api/design-requests/${id}/restore`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: designRequestKeys.all })
    },
  })
}

/**
 * 2026-08-02 [業務承認: matsushima-y owner裁定] プラン依頼（orphan 行）の復元。
 */
export function useRestorePlanRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<void>(`/api/plan-requests/${id}/restore`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: designRequestKeys.all })
    },
  })
}
